/**
 * Date disambiguation
 * --------------------
 * Tables store dates as yyyy-mm-dd, but parties print them in many layouts and the
 * same string can be ambiguous (05/06/12 = May 6, Jun 5, or … '12). This resolves a
 * raw printed date token to ISO, using, in priority order:
 *   1. self-disambiguation from the digits (a part >12 is the day; a 4-digit part is
 *      the year; a month name is explicit) — confident, no party needed;
 *   2. the PARTY's learned format (site_busi_date_format);
 *   3. the COUNTRY default format (site_mstr_countries.default_date_format_id);
 *   4. otherwise → ambiguous (the UI asks the user, then we LEARN the party's format).
 *
 * Pure + unit-tested (dateDisambiguate.test.ts). The `formats` arg is the
 * site_mstr_date_formats catalog; party/country are date_format_id values.
 */

export interface DateFormatRow {
  date_format_id: number;
  format_code: string;
  token_order: string;   // 'YMD' | 'MDY' | 'DMY'
  separator?: string;
  year_digits?: number;
  month_style?: string;  // 'numeric' | 'short' | 'long'
}

export type TokenOrder = 'YMD' | 'MDY' | 'DMY';

export interface InterpretResult {
  iso: string;                 // yyyy-mm-dd, or '' if unresolved
  order: TokenOrder | null;    // the resolved token order (null when ambiguous)
  formatId: number | null;     // best-matching catalog id for the resolved order
  ambiguous: boolean;          // true => needs user confirmation
  source: 'self' | 'party' | 'country' | 'none';
  candidates: { order: TokenOrder; iso: string }[];  // when ambiguous, the plausible reads
}

const MONTHS: Record<string, number> = {
  jan: 1, january: 1, feb: 2, february: 2, mar: 3, march: 3, apr: 4, april: 4,
  may: 5, jun: 6, june: 6, jul: 7, july: 7, aug: 8, august: 8,
  sep: 9, sept: 9, september: 9, oct: 10, october: 10, nov: 11, november: 11, dec: 12, december: 12,
};

const pad = (n: number) => String(n).padStart(2, '0');
const normYear = (y: number, len: number) => (len >= 3 ? y : 2000 + y);

function toIso(y: number, m: number, d: number): string | null {
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

function orderOf(formats: DateFormatRow[], id?: number | null): TokenOrder | null {
  if (!id) return null;
  const f = formats.find(x => x.date_format_id === id);
  const o = f?.token_order as TokenOrder | undefined;
  return o === 'YMD' || o === 'MDY' || o === 'DMY' ? o : null;
}

/** Pick the catalog id that best matches a resolved (order, separator, year length). */
export function findFormatId(formats: DateFormatRow[], order: TokenOrder, separator?: string, yearDigits?: number): number | null {
  const sameOrder = formats.filter(f => f.token_order === order);
  if (!sameOrder.length) return null;
  const exact = sameOrder.find(f =>
    (separator == null || f.separator === separator) &&
    (yearDigits == null || f.year_digits === yearDigits));
  if (exact) return exact.date_format_id;
  const sep = sameOrder.find(f => separator != null && f.separator === separator);
  return (sep || sameOrder[0]).date_format_id;
}

function parseMonthName(raw: string, formats: DateFormatRow[]): InterpretResult | null {
  const t = raw.trim().toLowerCase();
  // "Month DD, YYYY" / "Mon DD YYYY"  (MDY)
  let m = t.match(/^([a-z]+)\.?\s+(\d{1,2}),?\s+(\d{2,4})$/);
  if (m && MONTHS[m[1]]) {
    const iso = toIso(normYear(+m[3], m[3].length), MONTHS[m[1]], +m[2]);
    if (iso) return { iso, order: 'MDY', formatId: findFormatId(formats, 'MDY'), ambiguous: false, source: 'self', candidates: [{ order: 'MDY', iso }] };
  }
  // "DD Month YYYY"  (DMY)
  m = t.match(/^(\d{1,2})\s+([a-z]+)\.?,?\s+(\d{2,4})$/);
  if (m && MONTHS[m[2]]) {
    const iso = toIso(normYear(+m[3], m[3].length), MONTHS[m[2]], +m[1]);
    if (iso) return { iso, order: 'DMY', formatId: findFormatId(formats, 'DMY'), ambiguous: false, source: 'self', candidates: [{ order: 'DMY', iso }] };
  }
  return null;
}

/**
 * Interpret a raw printed date token to ISO.
 * opts: { formats, partyFormatId?, countryFormatId? }
 */
export function interpretDate(
  raw: string,
  opts: { formats: DateFormatRow[]; partyFormatId?: number | null; countryFormatId?: number | null },
): InterpretResult {
  const blank: InterpretResult = { iso: '', order: null, formatId: null, ambiguous: false, source: 'none', candidates: [] };
  const text = (raw || '').trim();
  if (!text) return blank;
  const formats = opts.formats || [];
  const partyOrder = orderOf(formats, opts.partyFormatId);
  const countryOrder = orderOf(formats, opts.countryFormatId);

  // Already ISO?
  const iso0 = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso0) {
    const got = toIso(+iso0[1], +iso0[2], +iso0[3]);
    if (got) return { iso: got, order: 'YMD', formatId: findFormatId(formats, 'YMD', '-', 4), ambiguous: false, source: 'self', candidates: [{ order: 'YMD', iso: got }] };
  }

  // Month-name forms are explicit.
  const mn = parseMonthName(text, formats);
  if (mn) return mn;

  // Numeric: three parts separated by / - .
  const m = text.match(/^(\d{1,4})([/\-.])(\d{1,2})[/\-.](\d{1,4})$/);
  if (!m) return blank;
  const sep = m[2];
  const A = +m[1], B = +m[3], C = +m[4];
  const la = m[1].length, lc = m[4].length;
  const fourIdx = [la, m[3].length, lc].findIndex(l => l === 4);

  const prefDM: TokenOrder | null =
    partyOrder === 'DMY' || partyOrder === 'MDY' ? partyOrder
    : countryOrder === 'DMY' || countryOrder === 'MDY' ? countryOrder : null;
  const prefSource: 'party' | 'country' = partyOrder === 'DMY' || partyOrder === 'MDY' ? 'party' : 'country';

  // Helper: resolve a "year-last" pair (A,B) with year yLen-digit at C.
  const resolveYearLast = (): InterpretResult => {
    const y = normYear(C, lc);
    if (A > 12 && B <= 12) { const iso = toIso(y, B, A); return iso ? mk(iso, 'DMY', 'self') : blank; }
    if (B > 12 && A <= 12) { const iso = toIso(y, A, B); return iso ? mk(iso, 'MDY', 'self') : blank; }
    if (A <= 12 && B <= 12) {
      if (prefDM) { const iso = prefDM === 'DMY' ? toIso(y, B, A) : toIso(y, A, B); return iso ? mk(iso, prefDM, prefSource) : blank; }
      const dmy = toIso(y, B, A), mdy = toIso(y, A, B);
      const cands = [dmy && { order: 'DMY' as TokenOrder, iso: dmy }, mdy && { order: 'MDY' as TokenOrder, iso: mdy }].filter(Boolean) as { order: TokenOrder; iso: string }[];
      return { iso: '', order: null, formatId: null, ambiguous: true, source: 'none', candidates: cands };
    }
    return blank; // both > 12 → invalid
  };
  const mk = (iso: string, order: TokenOrder, source: 'self' | 'party' | 'country'): InterpretResult =>
    ({ iso, order, formatId: findFormatId(formats, order, sep, lc >= 3 || la >= 3 ? 4 : 2), ambiguous: false, source, candidates: [{ order, iso }] });

  if (fourIdx === 0) { // YMD (4-digit year first)
    const y = normYear(A, la);
    const [mm, dd] = B > 12 && C <= 12 ? [C, B] : [B, C];
    const iso = toIso(y, mm, dd);
    return iso ? mk(iso, 'YMD', 'self') : blank;
  }
  if (fourIdx === 2) return resolveYearLast();          // year last (4-digit)
  if (fourIdx === 1) return blank;                       // 4-digit in the middle → unsupported

  // No 4-digit part: YMD only if a prior says so, else assume year-last.
  if (partyOrder === 'YMD' || countryOrder === 'YMD') {
    const y = normYear(A, la);
    const [mm, dd] = B > 12 && C <= 12 ? [C, B] : [B, C];
    const iso = toIso(y, mm, dd);
    return iso ? mk(iso, 'YMD', partyOrder === 'YMD' ? 'party' : 'country') : blank;
  }
  return resolveYearLast();
}
